import React, { useState, useEffect } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Skeleton,
  Snackbar, Alert, InputAdornment, Tooltip, Switch, FormControlLabel,
  Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination,
} from "@mui/material";
import { Plus, Pencil, Trash2, X, User, Mail, Phone, Shield, Eye, EyeOff, Search, Lock } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import { adminGetUsers, adminCreateUser, adminUpdateUser, adminDeleteUser } from "../../api/users";
import api from "../../api/axios";

// Add missing GET-by-ID function
const adminGetUser = (id) => api.get(`/api/admin/users/${id}/`);

const ROLES = ["SUPER_ADMIN", "ADMIN", "CONTENT_MANAGER", "MARKETING", "SALES"];

const ROLE_STYLES = {
  SUPER_ADMIN:      { bg: "rgba(239,68,68,0.08)",   color: "#DC2626" },
  ADMIN:           { bg: "rgba(139,92,246,0.08)",  color: "#7C3AED" },
  CONTENT_MANAGER: { bg: "rgba(37,99,235,0.08)",   color: "#2563EB" },
  MARKETING:       { bg: "rgba(34,197,94,0.08)",   color: "#16A34A" },
  SALES:           { bg: "rgba(245,158,11,0.08)",  color: "#B45309" },
};

const STATUS_STYLES = {
  true:  { bg: "rgba(34,197,94,0.1)",  color: "#16A34A", label: "Active" },
  false: { bg: "rgba(239,68,68,0.1)",  color: "#DC2626", label: "Inactive" },
};

const DEFAULT_VALUES = {
  full_name: "",
  email: "",
  phone: "",
  role: "CONTENT_MANAGER",
  password: "",
  confirm_password: "",
  is_active: true,
};

export default function UsersPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Queries
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => adminGetUsers().then(r => r.data),
  });

  // Fetch single user for edit
  const { data: editUserData, isLoading: isLoadingEdit } = useQuery({
    queryKey: ["admin-user", editing?.id],
    queryFn: () => adminGetUser(editing?.id).then(r => r.data),
    enabled: !!editing?.id && open,
    staleTime: 0,
  });

  // Update form when edit data loads
  useEffect(() => {
    if (editUserData && editing) {
      reset({
        full_name: editUserData.full_name || "",
        email: editUserData.email || "",
        phone: editUserData.phone || "",
        role: editUserData.role || "CONTENT_MANAGER",
        password: "",
        confirm_password: "",
        is_active: editUserData.is_active ?? true,
      });
    }
  }, [editUserData, editing, reset]);

  const createMutation = useMutation({
    mutationFn: adminCreateUser,
    onSuccess: () => { 
      qc.invalidateQueries(["admin-users"]); 
      notify("User created"); 
      closeDialog(); 
    },
    onError: (e) => notify(e?.response?.data?.error || "Failed to create user", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateUser(id, data),
    onSuccess: () => { 
      qc.invalidateQueries(["admin-users"]); 
      qc.invalidateQueries(["admin-user", editing?.id]);
      notify("User updated"); 
      closeDialog(); 
      setEditing(null); 
    },
    onError: (e) => notify(e?.response?.data?.error || "Failed to update user", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteUser,
    onSuccess: () => { 
      qc.invalidateQueries(["admin-users"]); 
      notify("User deactivated"); 
      setConfirmDelete(null); 
    },
    onError: (e) => { 
      notify(e?.response?.data?.error || "Cannot deactivate user", "error"); 
      setConfirmDelete(null); 
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    if (editing) {
      const { full_name, phone, role, is_active } = values;
      updateMutation.mutate({ id: editing.id, data: { full_name, phone, role, is_active } });
    } else {
      createMutation.mutate(values);
    }
  };

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false); 
    setEditing(null); 
    reset(DEFAULT_VALUES);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const startIndex = page * rowsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + rowsPerPage);

  return (
    <AdminShell title="Users">
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Manage user accounts and permissions. Set role and activation status for each user.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)", flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" } }}
        >
          Add User
        </Button>
      </Box>

      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2.5 }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Search size={18} color="#94A3B8" />
          <TextField
            fullWidth
            placeholder="Search users by name, email, phone, or role..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            InputProps={{ sx: { fontSize: 13.5 } }}
          />
        </Box>
      </Card>

      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["User", "Email", "Phone", "Role", "Status", ""].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, letterSpacing: "0.03em", textTransform: "uppercase", color: "text.secondary", borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 150 : j === 1 ? 200 : 100} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <User size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      {searchQuery ? "No matching users found" : "No users yet"}
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search." : "Create your first user to get started."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.map((u) => {
                const statusStyle = STATUS_STYLES[u.is_active];
                const roleStyle = ROLE_STYLES[u.role] || ROLE_STYLES.CONTENT_MANAGER;
                
                return (
                  <TableRow key={u.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: "9px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <User size={16} strokeWidth={2.1} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                          {u.full_name}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography sx={{ fontSize: 13, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {u.email}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                        {u.phone || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={u.role || "CONTENT_MANAGER"}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: roleStyle.bg, color: roleStyle.color }}
                      />
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={statusStyle.label}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small" 
                            onClick={() => openEdit(u)}
                            disabled={saving}
                            sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px", "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" } }}
                          >
                            <Pencil size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small" 
                            onClick={() => setConfirmDelete(u)}
                            disabled={deleteMutation.isPending}
                            sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px", "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" } }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        
        {!isLoading && filteredUsers.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": { pl: 2, pr: 1 },
              "& .MuiTablePagination-selectLabel": { fontSize: 13.5, color: "text.secondary" },
              "& .MuiTablePagination-displayedRows": { fontSize: 13.5, color: "text.secondary" },
            }}
          />
        )}
      </Card>

      <Dialog 
        open={open} 
        onClose={closeDialog} 
        maxWidth="md" 
        fullWidth 
        PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
      >
        <Box sx={{ 
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
          px: 3, py: 2.5,
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "white" }}>
              {editing ? "Edit User" : "New User"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
              {editing ? "Update user details and permissions." : "Create a new user account."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeDialog} disabled={saving} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <X size={20} />
          </IconButton>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
            {isLoadingEdit ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <User size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Account Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="full_name"
                      control={control}
                      rules={{ required: "Full name is required" }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Full Name"
                          fullWidth
                          disabled={saving}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{ startAdornment: <InputAdornment position="start"><User size={15} color="#94A3B8" /></InputAdornment> }}
                        />
                      )}
                    />
                    <Controller
                      name="email"
                      control={control}
                      rules={editing ? {} : { required: "Email is required", pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address" } }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Email"
                          type="email"
                          fullWidth
                          disabled={saving || editing}
                          error={!!fieldState.error}
                          helperText={editing ? "Email cannot be changed" : fieldState.error?.message}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Mail size={15} color="#94A3B8" /></InputAdornment> }}
                        />
                      )}
                    />
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Phone"
                          fullWidth
                          disabled={saving}
                          placeholder="Optional"
                          InputProps={{ startAdornment: <InputAdornment position="start"><Phone size={15} color="#94A3B8" /></InputAdornment> }}
                        />
                      )}
                    />
                  </Box>
                </Card>

                {!editing && (
                  <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                      <Lock size={16} color="#2563EB" />
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                        Credentials
                      </Typography>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                      <Controller
                        name="password"
                        control={control}
                        rules={{ required: "Password is required", minLength: { value: 8, message: "Minimum 8 characters" } }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Password"
                            type="password"
                            fullWidth
                            disabled={saving}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Lock size={15} color="#94A3B8" /></InputAdornment> }}
                          />
                        )}
                      />
                      <Controller
                        name="confirm_password"
                        control={control}
                        rules={{ required: "Please confirm password", validate: value => value === watch("password") || "Passwords do not match" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Confirm Password"
                            type="password"
                            fullWidth
                            disabled={saving}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Lock size={15} color="#94A3B8" /></InputAdornment> }}
                          />
                        )}
                      />
                    </Box>
                  </Card>
                )}

                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Shield size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Permissions
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="role"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Role"
                          fullWidth
                          disabled={saving}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Shield size={15} color="#94A3B8" /></InputAdornment> }}
                        >
                          {ROLES.map(r => (
                            <MenuItem key={r} value={r}>
                              <Chip label={r} size="small" sx={{ fontWeight: 600, fontSize: 12, height: 20, bgcolor: ROLE_STYLES[r]?.bg || "rgba(107,114,128,0.1)", color: ROLE_STYLES[r]?.color || "#6B7280" }} />
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} disabled={saving} color="primary" />}
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {field.value ? <Eye size={16} color="#16A34A" /> : <EyeOff size={16} color="#DC2626" />}
                              <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                                {field.value ? "User is active" : "User is inactive"}
                              </Typography>
                            </Box>
                          }
                          disabled={saving}
                        />
                      )}
                    />
                  </Box>
                </Card>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
            <Button
              type="submit" 
              variant="contained" 
              disabled={saving}
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#2563EB", px: 2.5, minWidth: 100, "&:hover": { bgcolor: "#1D4ED8" } }}
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => !deleteMutation.isPending && setConfirmDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Deactivate this user?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.full_name}" will be deactivated and will no longer have access to the system. This can be reversed later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleteMutation.isPending} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending} 
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#EF4444", px: 2.5, minWidth: 100, "&:hover": { bgcolor: "#DC2626" } }}
          >
            {deleteMutation.isPending ? "Deactivating..." : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}